import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import {
  fetchRoomTypes, createRoomType, updateRoomType, deleteRoomType,
  fetchRooms, createRoom, updateRoom, deleteRoom, updateRoomStatus,
  createProperty, updateProperty, getTenantSlug, getCurrentUser,
  truncateAuditLogs, uploadPropertyLogo, deletePropertyLogo,
  getToken,
} from '../../services/api.js';
import { Card, Btn, Field, Input, Select, Modal, Table, TR, TD, Badge, Toast, Spinner, fmtCurrency, useOfflineInvalidation } from '../../components/ui.jsx';
import { DEFAULT_PROFORMA_TERMS, DEFAULT_PAYMENT_REQUEST_BODY } from '../../utils/invoiceUtils.js';

const PROPERTY_TYPES = [
  {id:'hotel',label:'Hotel'},{id:'guesthouse',label:'Guesthouse / B&B'},
  {id:'hostel',label:'Hostel'},{id:'lodge',label:'Lodge / Safari Camp'},
  {id:'resort',label:'Resort'},{id:'apartment',label:'Serviced Apartment'},{id:'motel',label:'Motel'}
];
const CURRENCIES = ['USD','EUR','RWF','KES','TZS','UGX','ETB','ZAR','GBP','AED','NGN'];
const TIMEZONES  = ['Africa/Kigali','Africa/Nairobi','Africa/Dar_es_Salaam','Africa/Kampala',
  'Africa/Lagos','Africa/Johannesburg','Europe/London','Europe/Paris','Asia/Dubai','America/New_York'];
const ROOM_STATUSES = ['available','occupied','dirty','inspected','maintenance','blocked'];
const STATUS_META = {
  available:   { color:'#1A7A4A', bg:'#E8F7EE', icon:'✓' },
  occupied:    { color:'#C0392B', bg:'#FDECEA', icon:'●' },
  dirty:       { color:'#E07B00', bg:'#FFF3E0', icon:'~' },
  inspected:   { color:'#1A7F8E', bg:'#E0F5F8', icon:'✓' },
  maintenance: { color:'#6B7A8D', bg:'#F0F4F8', icon:'⚙' },
  blocked:     { color:'#3A4A5C', bg:'#EEF1F5', icon:'✕' },
};
const EMPTY_RT   = {name:'',code:'',category:'standard',maxAdults:2,maxChildren:1,maxOccupancy:3,
  baseRate:'',extraPersonRate:'',bedConfiguration:'King Bed',amenities:'',description:'',size:'',sortOrder:0};
const EMPTY_ROOM = {roomNumber:'',floor:'1',wing:'',status:'available',roomTypeId:'',notes:''};

const STAR_ICONS = {1:'★',2:'★★',3:'★★★',4:'★★★★',5:'★★★★★'};

const TABS = [
  { id:'property', label:'Property Info',  icon:'🏨', desc:'Basic details' },
  { id:'roomtypes',label:'Room Types',     icon:'🛏️', desc:'Categories & rates' },
  { id:'rooms',    label:'Rooms',          icon:'🚪', desc:'Individual rooms' },
  { id:'policies', label:'Policies',       icon:'📋', desc:'Descriptions & rules' },
  { id:'invoicing',label:'Invoicing & Tax',icon:'🧾', desc:'TIN, VAT & bank details' },
  { id:'advanced', label:'Advanced',       icon:'⚙️', desc:'Logging & system' },
];

/* ── small reusable section title ─────────────────────────────────────────── */
function SectionTitle({ icon, title, action }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
      <div style={{ display:'flex', alignItems:'center', gap:9 }}>
        <span style={{ fontSize:18 }}>{icon}</span>
        <span style={{ fontSize:14, fontWeight:700, color:T.navy }}>{title}</span>
      </div>
      {action}
    </div>
  );
}

/* ── info row ─────────────────────────────────────────────────────────────── */
function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'7px 0', borderBottom:`1px solid ${T.border}` }}>
      <span style={{ fontSize:14, width:22, textAlign:'center', flexShrink:0 }}>{icon}</span>
      <div>
        <p style={{ fontSize:10, color:T.muted, fontWeight:600, textTransform:'uppercase', letterSpacing:.6 }}>{label}</p>
        <p style={{ fontSize:13, color:T.navy, marginTop:2 }}>{value}</p>
      </div>
    </div>
  );
}

/* ── NoBg placeholder ─────────────────────────────────────────────────────── */
function NoBg({ icon, title, sub, action }) {
  return (
    <div style={{ background:T.surface, border:`2px dashed ${T.border}`, borderRadius:16, padding:48, textAlign:'center' }}>
      <div style={{ fontSize:42, marginBottom:12 }}>{icon}</div>
      <p style={{ fontSize:15, fontWeight:700, color:T.slate, marginBottom:6 }}>{title}</p>
      <p style={{ fontSize:12, color:T.muted, marginBottom:20 }}>{sub}</p>
      {action}
    </div>
  );
}

/* ── Room type card ───────────────────────────────────────────────────────── */
function RtCard({ rt, currency, onEdit, onDelete }) {
  const amenities = rt.amenities?.split(',').map(a=>a.trim()).filter(Boolean) || [];
  return (
    <div style={{
      background:T.white, borderRadius:16, border:`1px solid ${T.border}`,
      overflow:'hidden', boxShadow:SHADOW.sm,
      transition:'box-shadow .2s, transform .2s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = SHADOW.md; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = SHADOW.sm; e.currentTarget.style.transform = ''; }}>
      {/* colour band */}
      <div style={{ height:4, background:`linear-gradient(90deg, ${T.teal}, ${T.navy})` }} />
      <div style={{ padding:'16px 18px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <p style={{ fontSize:15, fontWeight:700, color:T.navy }}>{rt.name}</p>
              {rt.code && <Badge label={rt.code} color={T.teal} />}
            </div>
            <p style={{ fontSize:11, color:T.muted, marginTop:3 }}>
              {rt.bedConfiguration}{rt.size ? ` · ${rt.size}m²` : ''}
            </p>
          </div>
          <div style={{ textAlign:'right' }}>
            <p style={{ fontFamily:FONTS.display, fontSize:18, fontWeight:800, color:T.teal, lineHeight:1 }}>
              {fmtCurrency(rt.baseRate||0, currency)}
            </p>
            <p style={{ fontSize:9, color:T.muted, letterSpacing:.5 }}>/NIGHT</p>
          </div>
        </div>

        <div style={{ display:'flex', gap:16, marginBottom:10 }}>
          <span style={{ fontSize:11, color:T.slate }}>👤 {rt.maxAdults} adults</span>
          {rt.maxChildren > 0 && <span style={{ fontSize:11, color:T.slate }}>🧒 {rt.maxChildren} children</span>}
          <span style={{ fontSize:11, color:T.slate }}>🏠 max {rt.maxOccupancy}</span>
        </div>

        {rt.extraPersonRate > 0 && (
          <div style={{
            display:'flex', justifyContent:'space-between', alignItems:'center',
            padding:'5px 8px', borderRadius:8, background:'#FEF9C3',
            border:'1px solid #FDE68A', marginBottom:10,
          }}>
            <span style={{ fontSize:10, color:'#78350F' }}>👥 Extra person rate (&gt;{rt.maxOccupancy} guests)</span>
            <span style={{ fontSize:11, fontWeight:700, color:'#78350F' }}>{fmtCurrency(rt.extraPersonRate, currency)}</span>
          </div>
        )}

        {amenities.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:12 }}>
            {amenities.slice(0,6).map((a,i) => (
              <span key={i} style={{
                fontSize:9, background:`${T.teal}14`, color:T.teal,
                padding:'3px 8px', borderRadius:99, fontWeight:600,
              }}>{a}</span>
            ))}
            {amenities.length > 6 && (
              <span style={{ fontSize:9, color:T.muted, padding:'3px 4px' }}>+{amenities.length-6}</span>
            )}
          </div>
        )}

        <div style={{ display:'flex', gap:8 }}>
          <Btn size="sm" variant="ghost" style={{ flex:1 }} onClick={() => onEdit(rt)}>✏️ Edit</Btn>
          <Btn size="sm" variant="secondary" onClick={() => onDelete(rt)}>🗑️</Btn>
        </div>
      </div>
    </div>
  );
}


// ─── Desktop App install section (used in Advanced tab) ──────────────────────
function DesktopAppSection() {
  const [loading,    setLoading]    = React.useState(false);
  const [error,      setError]      = React.useState(null);
  const [info,       setInfo]       = React.useState(null);
  const [downloaded, setDownloaded] = React.useState(false);
  const [open,       setOpen]       = React.useState(false);

  const tenantSlug = getTenantSlug();
  const platform   = (() => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('win'))  return 'win32';
    if (ua.includes('mac'))  return 'darwin';
    return 'linux';
  })();
  const PLATFORM_LABELS = { win32: '🪟 Windows', darwin: '🍎 macOS', linux: '🐧 Linux' };

  const fetchInfo = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/desktop/installer?tenant=${encodeURIComponent(tenantSlug)}&platform=${platform}`,
        { headers: { Authorization: `Bearer ${getToken() || ''}`, 'X-Tenant-Slug': tenantSlug, Accept: 'application/json' } }
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setInfo(data.data ?? data);
    } catch (e) {
      setError(e.message || 'Could not reach server');
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, platform]);

  const handleDownload = () => {
    if (!info?.downloadUrl) return;
    const a = document.createElement('a');
    a.href = info.downloadUrl;
    a.download = info.filename || `${tenantSlug}-${info?.version || '1.0.0'}-setup.exe`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setDownloaded(true);
  };

  return (
    <div style={{ background: T.white, borderRadius: 16, border: `1px solid ${T.border}`, padding: '20px 22px', boxShadow: SHADOW.sm }}>
      <SectionTitle icon="💻" title="Desktop App" />
      <p style={{ fontSize: 12, color: T.muted, marginBottom: 16, lineHeight: 1.6 }}>
        The desktop app runs offline using a local SQLite database and syncs automatically
        when your network is restored. It is pre-configured for <strong>{tenantSlug}</strong>
        — no manual setup required after installation.
      </p>

      {/* Features list */}
      <div style={{ background: `${T.teal}0d`, border: `1px solid ${T.teal}30`,
        borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
        <ul style={{ fontSize: 12, color: T.muted, paddingLeft: 18, lineHeight: 2, margin: 0 }}>
          <li>✅ Fully offline — check-in and manage reservations without internet</li>
          <li>✅ All changes sync automatically when you reconnect</li>
          <li>✅ Same staff credentials as the browser version</li>
          <li>✅ Pre-built for <strong>{tenantSlug}</strong> — no configuration needed</li>
        </ul>
      </div>

      {/* Action row */}
      {!open ? (
        <Btn variant="primary" onClick={() => { setOpen(true); fetchInfo(); }}>
          💻 Install Desktop App
        </Btn>
      ) : (
        <div style={{ background: T.surface, borderRadius: 12, border: `1px solid ${T.border}`,
          padding: '18px 20px', marginTop: 4 }}>

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10,
              fontSize: 13, color: T.muted, padding: '8px 0' }}>
              <Spinner size={16} /> Preparing installer for <strong>{tenantSlug}</strong>…
            </div>
          )}

          {error && !loading && (
            <div style={{ background: `${T.danger}12`, border: `1px solid ${T.danger}40`,
              borderRadius: 10, padding: '12px 16px', marginBottom: 14,
              fontSize: 13, color: T.danger }}>
              ⚠️ {error}
              <button onClick={fetchInfo} style={{ marginLeft: 10, background: 'none',
                border: 'none', cursor: 'pointer', color: T.teal, fontSize: 12, fontWeight: 600 }}>
                Retry
              </button>
            </div>
          )}

          {info && !loading && (
            <>
              {info.available === false ? (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a',
                  borderRadius: 10, padding: '12px 16px', marginBottom: 14,
                  fontSize: 13, color: '#92400e' }}>
                  🛠️ The installer for <strong>{tenantSlug}</strong> is being prepared.
                  Once released, the download button will appear here automatically.
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '8px 24px', fontSize: 13, marginBottom: 16 }}>
                    {[
                      ['Hotel',    tenantSlug],
                      ['Platform', PLATFORM_LABELS[platform] ?? platform],
                      ['Version',  info.version  || '—'],
                      ['File',     info.filename || '—'],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.muted,
                          textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
                        <div style={{ fontSize: 13, color: T.navy, fontWeight: 600, marginTop: 1 }}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {downloaded && (
                    <div style={{ background: `${T.teal}12`, border: `1px solid ${T.teal}44`,
                      borderRadius: 10, padding: '12px 16px', marginBottom: 14,
                      fontSize: 13, color: T.teal }}>
                      ✅ Download started. Run the installer and then open the app.
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: info && !loading ? 0 : 8 }}>
            <Btn variant="ghost" onClick={() => { setOpen(false); setDownloaded(false); setInfo(null); setError(null); }}>
              Close
            </Btn>
            {info && info.available !== false && !loading && (
              <Btn onClick={handleDownload} disabled={!info?.downloadUrl}>
                {downloaded ? '⬇️ Download Again' : '⬇️ Download Installer'}
              </Btn>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PropertySetup({ property: existingProp, onPropertyCreated, onPropertyUpdated }) {
  const [tab,      setTab]      = useState('property');
  const [property, setProperty] = useState(existingProp||null);
  const [form,     setForm]     = useState({
    name:'',type:'hotel',starRating:3,addressLine1:'',city:'',country:'Rwanda',currency:'RWF',
    timezone:'Africa/Kigali',checkInTime:'14:00',checkOutTime:'11:00',phone:'',email:'',
    website:'',description:'',amenities:'',policies:'',
    noShowPolicy:'NONE', noShowFeeAmount:'', noShowFeePercentage:'', noShowGraceHours:0,
    auditLoggingEnabled: true,
    tin:'', invoiceFooterText:'', vatRate:18, tourismTaxRate:3, logoUrl:'',
    proformaTermsText: DEFAULT_PROFORMA_TERMS, paymentRequestBodyTemplate: DEFAULT_PAYMENT_REQUEST_BODY,
    paymentRequestSignerRole:'Manager',
    tenantSlug: getTenantSlug() || '',
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileRef = useRef(null);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState('');
  const [roomTypes,setRoomTypes]= useState([]);
  const [rooms,    setRooms]    = useState([]);
  const [rtLoad,   setRtLoad]   = useState(false);
  const [rLoad,    setRLoad]    = useState(false);
  const [rtModal,  setRtModal]  = useState(false);
  const [rModal,   setRModal]   = useState(false);
  const [editRt,   setEditRt]   = useState(null);
  const [editRoom, setEditRoom] = useState(null);
  const [rtForm,   setRtForm]   = useState({...EMPTY_RT});
  const [rForm,    setRForm]    = useState({...EMPTY_ROOM});
  const [truncating, setTruncating] = useState(false);

  const pid      = property?.id;
  const currency = property?.currency || form.currency || 'USD';
  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''), 3500); };

  useEffect(() => {
    if (existingProp) {
      setProperty(existingProp);
      setForm(f => ({
        ...f,
        ...existingProp,
        proformaTermsText: existingProp.proformaTermsText || DEFAULT_PROFORMA_TERMS,
        paymentRequestBodyTemplate: existingProp.paymentRequestBodyTemplate || DEFAULT_PAYMENT_REQUEST_BODY,
        paymentRequestSignerRole: existingProp.paymentRequestSignerRole || 'Manager',
      }));
    }
  }, [existingProp]);

  const loadRt = useCallback(() => {
    if (!pid) return; setRtLoad(true);
    fetchRoomTypes(pid).then(d=>setRoomTypes(Array.isArray(d)?d:[])).finally(()=>setRtLoad(false));
  }, [pid]);
  const loadR  = useCallback(() => {
    if (!pid) return; setRLoad(true);
    fetchRooms(pid).then(d=>setRooms(Array.isArray(d)?d:[])).finally(()=>setRLoad(false));
  }, [pid]);

  useEffect(() => { if (tab==='roomtypes') loadRt(); }, [tab, pid]);
  useEffect(() => { if (tab==='rooms') { loadR(); if(!roomTypes.length) loadRt(); } }, [tab, pid]);
  useOfflineInvalidation('rooms', loadR);
  useOfflineInvalidation('room-types', loadRt);

  const handleTruncateAuditLogs = async () => {
    if (!pid || truncating) return;
    const ok = window.confirm(
      'Delete ALL audit logs for this property?\n\nThis cannot be undone. Only use this to clear old noise before the new logging rules take effect.'
    );
    if (!ok) return;
    setTruncating(true);
    try {
      const result = await truncateAuditLogs(pid);
      const deleted = result?.deleted ?? 0;
      showToast(`✅ Removed ${deleted.toLocaleString()} audit log entries`);
    } catch (e) {
      showToast(`❌ ${e.message || 'Failed to clear audit logs'}`);
    } finally {
      setTruncating(false);
    }
  };

  const saveProperty = async () => {
    if (!form.name?.trim()) { showToast('Property name required'); return; }
    setSaving(true);
    try {
      const jwtTenantSlug = getTenantSlug() || getCurrentUser()?.tenantSlug || '';
      const cleanSlug = jwtTenantSlug.replace(/[^a-z0-9]/gi, '').toLowerCase();
      const payload = {
        ...form,
        active: true,
        slug: cleanSlug || form.slug?.replace(/[^a-z0-9]/g,'') || form.name.toLowerCase().replace(/[^a-z0-9]/g,''),
        // Parse numeric tax fields so Jackson receives numbers not strings
        vatRate:        form.vatRate        !== '' && form.vatRate        != null ? parseFloat(form.vatRate)        : form.vatRate,
        tourismTaxRate: form.tourismTaxRate !== '' && form.tourismTaxRate != null ? parseFloat(form.tourismTaxRate) : form.tourismTaxRate,
      };
      const saved = pid ? await updateProperty(pid, payload) : await createProperty(payload);
      setProperty(saved); setForm(f=>({...f,...saved}));
      showToast(`\u2705 Property ${pid?'updated':'created'}`);
      if (!pid) onPropertyCreated?.(saved);
      else onPropertyUpdated?.(saved); // keep App.jsx property state in sync so navigating away and back doesn't wipe the form
    } catch(e) { showToast(`\u274c ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (file) => {
    if (!pid) { showToast('Save your property first before adding a logo'); return; }
    if (!file) return;
    setLogoUploading(true);
    try {
      const { logoUrl } = await uploadPropertyLogo(pid, file);
      setForm(f=>({...f, logoUrl}));
      setProperty(p=>p?{...p, logoUrl}:p);
      showToast('✅ Logo updated');
    } catch(e) { showToast(`❌ ${e.message}`); }
    finally { setLogoUploading(false); if (logoFileRef.current) logoFileRef.current.value=''; }
  };

  const handleLogoRemove = async () => {
    if (!pid || !form.logoUrl) return;
    setLogoUploading(true);
    try {
      await deletePropertyLogo(pid);
      setForm(f=>({...f, logoUrl:''}));
      setProperty(p=>p?{...p, logoUrl:''}:p);
      showToast('Logo removed');
    } catch(e) { showToast(`❌ ${e.message}`); }
    finally { setLogoUploading(false); }
  };

  const saveRt = async () => {
    if (!rtForm.name?.trim()) { showToast('Name required'); return; }
    setSaving(true);
    try {
      const p = {...rtForm, baseRate:parseFloat(rtForm.baseRate)||0,
        extraPersonRate: rtForm.extraPersonRate!=='' && rtForm.extraPersonRate!=null ? parseFloat(rtForm.extraPersonRate)||0 : null,
        maxAdults:parseInt(rtForm.maxAdults)||2, maxChildren:parseInt(rtForm.maxChildren)||1,
        maxOccupancy:parseInt(rtForm.maxOccupancy)||3, sortOrder:parseInt(rtForm.sortOrder)||0,
        size:rtForm.size?parseInt(rtForm.size):null, active:true};
      if (editRt?.id) await updateRoomType(editRt.id, p);
      else            await createRoomType(pid, p);
      showToast(`✅ Room type ${editRt?'updated':'created'}`);
      setRtModal(false); loadRt();
    } catch(e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const deleteRt = async (rt) => {
    if (!window.confirm(`Delete "${rt.name}"?`)) return;
    try { await deleteRoomType(rt.id); showToast('Deleted'); loadRt(); }
    catch(e) { showToast(`❌ ${e.message}`); }
  };

  const saveRoom = async () => {
    if (!rForm.roomNumber?.trim()) { showToast('Room number required'); return; }
    if (!rForm.roomTypeId) { showToast('Room type required'); return; }
    setSaving(true);
    try {
      if (editRoom?.id) await updateRoom(editRoom.id, rForm);
      else              await createRoom(pid, rForm);
      showToast(`✅ Room ${editRoom?'updated':'added'}`);
      setRModal(false); loadR();
    } catch(e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const delRoom = async (r) => {
    if (!window.confirm(`Delete room ${r.roomNumber}?`)) return;
    try { await deleteRoom(r.id); showToast('Deleted'); loadR(); }
    catch(e) { showToast(`❌ ${e.message}`); }
  };

  const changeStatus = async (r, s) => {
    try { await updateRoomStatus(r.id, s); loadR(); }
    catch(e) { showToast(`❌ ${e.message}`); }
  };

  const sf   = (k, v) => setForm(f=>({...f,[k]:v}));
  const srf  = (k, v) => setRtForm(f=>({...f,[k]:v}));
  const srrf = (k, v) => setRForm(f=>({...f,[k]:v}));

  const activeTabMeta = TABS.find(t=>t.id===tab);

  return (
    <div className="fade-up">
      <style>{`
        .ps-nav-item { display:flex; align-items:center; gap:12px; padding:11px 14px; border-radius:11px; cursor:pointer; transition:all .18s; border:1px solid transparent; margin-bottom:4px; }
        .ps-nav-item:hover { background:${T.surface}; }
        .ps-nav-item.active { background:${T.navy}; border-color:${T.navy}; }
        .ps-nav-item.active .ps-nav-label { color:#fff; }
        .ps-nav-item.active .ps-nav-desc { color:rgba(255,255,255,.55); }
        .ps-nav-label { font-size:13px; font-weight:700; color:${T.navy}; }
        .ps-nav-desc { font-size:10px; color:${T.muted}; margin-top:1px; }
        .ps-rt-card:hover { box-shadow:${SHADOW.md}; transform:translateY(-2px); }
      `}</style>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div style={{
        background:`linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 100%)`,
        borderRadius:18, padding:'22px 28px', marginBottom:22,
        display:'flex', justifyContent:'space-between', alignItems:'center',
        flexWrap:'wrap', gap:14, boxShadow:SHADOW.lg,
      }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:5 }}>
            <span style={{ fontSize:22 }}>🏨</span>
            <h1 style={{ fontFamily:FONTS.display, fontSize:24, fontWeight:700, color:'#fff', margin:0 }}>
              {property?.name || 'Property Setup'}
            </h1>
          </div>
          <p style={{ fontSize:12, color:'rgba(255,255,255,.55)', margin:0 }}>
            {property
              ? `${PROPERTY_TYPES.find(t=>t.id===property.type)?.label||''} · ${[property.city,property.country].filter(Boolean).join(', ')} · ${currency}`
              : 'Configure your hotel before accepting reservations'}
          </p>
        </div>
        {property && (
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <div style={{ background:'rgba(255,255,255,.12)', borderRadius:10, padding:'8px 16px', textAlign:'center' }}>
              <p style={{ fontSize:20, fontWeight:800, color:'#fff', lineHeight:1 }}>{rooms.length||'—'}</p>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.6)', marginTop:2 }}>Rooms</p>
            </div>
            <div style={{ background:'rgba(255,255,255,.12)', borderRadius:10, padding:'8px 16px', textAlign:'center' }}>
              <p style={{ fontSize:20, fontWeight:800, color:'#fff', lineHeight:1 }}>{roomTypes.length||'—'}</p>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.6)', marginTop:2 }}>Types</p>
            </div>
            <div style={{ background:'rgba(255,255,255,.12)', borderRadius:10, padding:'8px 16px', textAlign:'center' }}>
              <p style={{ fontSize:16, fontWeight:800, color:'#fbbf24', lineHeight:1 }}>{STAR_ICONS[property.starRating]||'—'}</p>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.6)', marginTop:2 }}>Rating</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Body: sidebar + content ──────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'210px 1fr', gap:18, alignItems:'start' }}>

        {/* Sidebar */}
        <div style={{ position:'sticky', top:80 }}>
          {TABS.map(t => (
            <div key={t.id} className={`ps-nav-item${tab===t.id?' active':''}`}
              onClick={() => setTab(t.id)}>
              <span style={{ fontSize:18 }}>{t.icon}</span>
              <div>
                <p className="ps-nav-label">{t.label}</p>
                <p className="ps-nav-desc">{t.desc}</p>
              </div>
            </div>
          ))}

          {/* Property summary card when setup done */}
          {property && (
            <div style={{ marginTop:16, background:T.white, borderRadius:14, border:`1px solid ${T.border}`, padding:'14px 16px', boxShadow:SHADOW.sm }}>
              <p style={{ fontSize:10, color:T.muted, fontWeight:700, letterSpacing:.8, textTransform:'uppercase', marginBottom:10 }}>Summary</p>
              <InfoRow icon="🏙️" label="City"     value={[property.city,property.country].filter(Boolean).join(', ')} />
              <InfoRow icon="💱" label="Currency" value={currency} />
              <InfoRow icon="🕐" label="Check-in" value={`${property.checkInTime||'14:00'} / ${property.checkOutTime||'11:00'}`} />
              <InfoRow icon="📞" label="Phone"    value={property.phone} />
            </div>
          )}
        </div>

        {/* Main content */}
        <div>

          {/* Section breadcrumb */}
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:18, color:T.muted, fontSize:12 }}>
            <span>Setup</span>
            <span>›</span>
            <span style={{ fontWeight:700, color:T.navy }}>{activeTabMeta?.icon} {activeTabMeta?.label}</span>
          </div>

          {/* ── PROPERTY INFO ───────────────────────────────────────────── */}
          {tab === 'property' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                {/* Basic info */}
                <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                  <SectionTitle icon="📌" title="Identity" />
                  <Field label="Property Name" required>
                    <Input value={form.name||''} onChange={v=>sf('name',v)} placeholder="Grand Hotel Kigali" />
                  </Field>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <Field label="Property Type">
                      <Select value={form.type||'hotel'} onChange={v=>sf('type',v)}>
                        {PROPERTY_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                      </Select>
                    </Field>
                    <Field label="Star Rating">
                      <Select value={form.starRating||3} onChange={v=>sf('starRating',parseInt(v))}>
                        {[1,2,3,4,5].map(n=><option key={n} value={n}>{STAR_ICONS[n]} {n} Star{n>1?'s':''}</option>)}
                      </Select>
                    </Field>
                    <Field label="Currency">
                      <Select value={form.currency||'USD'} onChange={v=>sf('currency',v)}>
                        {CURRENCIES.map(c=><option key={c} value={c}>{c}</option>)}
                      </Select>
                    </Field>
                    <Field label="Timezone">
                      <Select value={form.timezone||'Africa/Kigali'} onChange={v=>sf('timezone',v)}>
                        {TIMEZONES.map(tz=><option key={tz} value={tz}>{tz.replace('Africa/','').replace('Europe/','').replace('Asia/','')}</option>)}
                      </Select>
                    </Field>
                  </div>
                </div>

                {/* Times */}
                <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                  <SectionTitle icon="🕐" title="Check-in / Check-out" />
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                    <Field label="Check-in Time">
                      <Input type="time" value={form.checkInTime||'14:00'} onChange={v=>sf('checkInTime',v)} />
                    </Field>
                    <Field label="Check-out Time">
                      <Input type="time" value={form.checkOutTime||'11:00'} onChange={v=>sf('checkOutTime',v)} />
                    </Field>
                  </div>
                  {/* Visual time display */}
                  <div style={{ background:`linear-gradient(135deg, ${T.navy}08, ${T.teal}05)`, borderRadius:10, padding:'12px 16px', display:'flex', justifyContent:'space-around', alignItems:'center' }}>
                    <div style={{ textAlign:'center' }}>
                      <p style={{ fontSize:22, fontFamily:FONTS.display, fontWeight:700, color:T.teal }}>{form.checkInTime||'14:00'}</p>
                      <p style={{ fontSize:10, color:T.muted, marginTop:2 }}>CHECK-IN</p>
                    </div>
                    <div style={{ fontSize:18, color:T.border }}>→</div>
                    <div style={{ textAlign:'center' }}>
                      <p style={{ fontSize:22, fontFamily:FONTS.display, fontWeight:700, color:T.navy }}>{form.checkOutTime||'11:00'}</p>
                      <p style={{ fontSize:10, color:T.muted, marginTop:2 }}>CHECK-OUT</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <SectionTitle icon="📍" title="Location & Contact" />
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:12 }}>
                  <Field label="Street Address">
                    <Input value={form.addressLine1||''} onChange={v=>sf('addressLine1',v)} placeholder="KN 5 Road, Kicukiro" />
                  </Field>
                  <Field label="City">
                    <Input value={form.city||''} onChange={v=>sf('city',v)} placeholder="Kigali" />
                  </Field>
                  <Field label="Country">
                    <Input value={form.country||''} onChange={v=>sf('country',v)} placeholder="Rwanda" />
                  </Field>
                  <Field label="Phone">
                    <Input value={form.phone||''} onChange={v=>sf('phone',v)} placeholder="+250 7xx xxx xxx" />
                  </Field>
                  <Field label="Email">
                    <Input type="email" value={form.email||''} onChange={v=>sf('email',v)} placeholder="info@hotel.com" />
                  </Field>
                  <Field label="Website">
                    <Input value={form.website||''} onChange={v=>sf('website',v)} placeholder="https://yourhotel.com" />
                  </Field>
                </div>
              </div>

              <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                <Btn variant="primary" size="lg" disabled={saving} onClick={saveProperty}>
                  {saving ? '⏳ Saving…' : pid ? '💾 Save Changes' : '✓ Create Property'}
                </Btn>
              </div>
            </div>
          )}

          {/* ── ROOM TYPES ──────────────────────────────────────────────── */}
          {tab === 'roomtypes' && (
            <div>
              {!pid && (
                <div style={{ background:T.warningPale, border:`1px solid ${T.warning}30`, borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', gap:10 }}>
                  <span>⚠️</span>
                  <p style={{ fontSize:13, color:T.warning }}>Save your property first before adding room types.</p>
                </div>
              )}

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div>
                  <p style={{ fontSize:15, fontWeight:700, color:T.navy }}>Room Types</p>
                  <p style={{ fontSize:11, color:T.muted }}>{roomTypes.length} configured</p>
                </div>
                <Btn variant="primary" disabled={!pid}
                  onClick={() => { setEditRt(null); setRtForm({...EMPTY_RT}); setRtModal(true); }}>
                  + Add Room Type
                </Btn>
              </div>

              {rtLoad ? <div style={{ textAlign:'center', padding:40 }}><Spinner /></div>
              : roomTypes.length === 0
              ? <NoBg icon="🛏️" title="No room types yet" sub="Define categories first — Standard, Deluxe, Suite…"
                  action={<Btn variant="primary" disabled={!pid}
                    onClick={() => { setEditRt(null); setRtForm({...EMPTY_RT}); setRtModal(true); }}>
                    + Add First Room Type
                  </Btn>} />
              : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
                  {roomTypes.map(rt => (
                    <RtCard key={rt.id} rt={rt} currency={currency}
                      onEdit={r => { setEditRt(r); setRtForm({...EMPTY_RT,...r}); setRtModal(true); }}
                      onDelete={deleteRt} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ROOMS ───────────────────────────────────────────────────── */}
          {tab === 'rooms' && (
            <div>
              {!pid && (
                <div style={{ background:T.warningPale, border:`1px solid ${T.warning}30`, borderRadius:12, padding:'12px 18px', marginBottom:16, display:'flex', gap:10 }}>
                  <span>⚠️</span>
                  <p style={{ fontSize:13, color:T.warning }}>Save your property first.</p>
                </div>
              )}

              {/* Status summary chips */}
              {rooms.length > 0 && (
                <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
                  {Object.entries(STATUS_META).map(([s, m]) => {
                    const n = rooms.filter(r=>r.status===s).length;
                    if (!n) return null;
                    return (
                      <div key={s} style={{ background:m.bg, border:`1px solid ${m.color}40`, borderRadius:99, padding:'6px 14px', display:'flex', gap:7, alignItems:'center' }}>
                        <div style={{ width:7, height:7, borderRadius:'50%', background:m.color }} />
                        <span style={{ fontSize:11, fontWeight:700, color:m.color, textTransform:'capitalize' }}>{s}</span>
                        <span style={{ fontSize:13, fontWeight:800, color:T.navy }}>{n}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div>
                  <p style={{ fontSize:15, fontWeight:700, color:T.navy }}>Rooms</p>
                  <p style={{ fontSize:11, color:T.muted }}>{rooms.length} configured</p>
                </div>
                <Btn variant="primary" disabled={!pid}
                  onClick={() => { setEditRoom(null); setRForm({...EMPTY_ROOM}); setRModal(true); }}>
                  + Add Room
                </Btn>
              </div>

              {rLoad ? <div style={{ textAlign:'center', padding:40 }}><Spinner /></div>
              : rooms.length === 0
              ? <NoBg icon="🚪" title="No rooms yet" sub="Add individual rooms with number, floor, and type."
                  action={<Btn variant="primary" disabled={!pid||roomTypes.length===0}
                    onClick={() => { setEditRoom(null); setRForm({...EMPTY_ROOM}); setRModal(true); }}>
                    {roomTypes.length === 0 ? 'Add Room Types First' : '+ Add First Room'}
                  </Btn>} />
              : (
                <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, overflow:'hidden', boxShadow:SHADOW.sm }}>
                  <Table headers={['Room','Floor / Wing','Type','Status','','']}>
                    {rooms.map(r => {
                      const rt = roomTypes.find(t=>t.id===r.roomTypeId);
                      const sm = STATUS_META[r.status] || STATUS_META.available;
                      return (
                        <TR key={r.id}>
                          <TD>
                            <p style={{ fontSize:15, fontWeight:700, color:T.navy }}>Room {r.roomNumber}</p>
                            {r.notes && <p style={{ fontSize:10, color:T.muted }}>{r.notes}</p>}
                          </TD>
                          <TD><span style={{ fontSize:12, color:T.slate }}>{r.floor?`Floor ${r.floor}`:'—'}{r.wing?` · ${r.wing}`:''}</span></TD>
                          <TD>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              {rt ? (
                                <>
                                  <span style={{ fontSize:12, fontWeight:600, color:T.navy }}>{rt.name}</span>
                                  {rt.code && <span style={{ fontSize:9, background:`${T.teal}18`, color:T.teal, padding:'2px 6px', borderRadius:99, fontWeight:700 }}>{rt.code}</span>}
                                </>
                              ) : (
                                <span style={{ fontSize:12, color:T.muted }}>—</span>
                              )}
                            </div>
                          </TD>
                          <TD>
                            <Select value={r.status} onChange={v=>changeStatus(r,v)}
                              style={{ width:130, fontSize:11, padding:'5px 8px', borderRadius:7, color:sm.color, fontWeight:700, background:sm.bg, border:`1px solid ${sm.color}40` }}>
                              {ROOM_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                            </Select>
                          </TD>
                          <TD>
                            <Btn size="sm" variant="ghost"
                              onClick={() => { setEditRoom(r); setRForm({...EMPTY_ROOM,...r}); setRModal(true); }}>✏️</Btn>
                          </TD>
                          <TD>
                            <Btn size="sm" variant="secondary" onClick={() => delRoom(r)}>🗑️</Btn>
                          </TD>
                        </TR>
                      );
                    })}
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* ── POLICIES ────────────────────────────────────────────────── */}
          {tab === 'policies' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                  <SectionTitle icon="✨" title="About the Property" />
                  <Field label="Hotel Description">
                    <Input rows={5} value={form.description||''} onChange={v=>sf('description',v)}
                      placeholder="A beautiful hotel nestled in the heart of the city..." />
                  </Field>
                  <Field label="Amenities (comma-separated)">
                    <Input rows={3} value={form.amenities||''} onChange={v=>sf('amenities',v)}
                      placeholder="Free WiFi, Pool, Gym, Spa, Restaurant, Parking..." />
                  </Field>
                </div>
                <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                  <SectionTitle icon="📋" title="House Policies" />
                  <Field label="Hotel Policies" hint="Shown on booking confirmations & receipts">
                    <Input rows={9} value={form.policies||''} onChange={v=>sf('policies',v)}
                      placeholder="No smoking in rooms.&#10;Check-in requires valid government-issued ID.&#10;Quiet hours: 22:00–07:00.&#10;Pets not allowed." />
                  </Field>
                </div>
              </div>
              {form.amenities && (
                <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'16px 20px', boxShadow:SHADOW.sm }}>
                  <p style={{ fontSize:12, fontWeight:700, color:T.navy, marginBottom:10 }}>Amenity Preview</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {form.amenities.split(',').map(a=>a.trim()).filter(Boolean).map((a,i) => (
                      <span key={i} style={{
                        fontSize:11, background:`${T.teal}14`, color:T.teal, fontWeight:600,
                        padding:'5px 12px', borderRadius:99, border:`1px solid ${T.teal}30`,
                      }}>✓ {a}</span>
                    ))}
                  </div>
                </div>
              )}
              {/* ── No-Show Policy ── */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <SectionTitle icon="⛔" title="No-Show Policy" />
                <p style={{ fontSize:12, color:T.muted, marginBottom:14, lineHeight:1.5 }}>
                  Define how to handle guests who fail to arrive on their reservation date.
                  The policy is suggested automatically when staff mark a reservation as no-show.
                </p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <Field label="Fee Policy" hint="Applied automatically when marking no-show">
                    <Select value={form.noShowPolicy||'NONE'} onChange={v=>sf('noShowPolicy',v)}>
                      <option value="NONE">No Charge — mark only</option>
                      <option value="FIRST_NIGHT">First Night Rate</option>
                      <option value="PERCENTAGE">Percentage of Total</option>
                      <option value="FIXED_AMOUNT">Fixed Amount</option>
                      <option value="FULL_BOOKING">Full Booking Amount</option>
                    </Select>
                  </Field>
                  <Field label="Grace Period (hours)" hint="Hours after arrival before staff can mark no-show">
                    <Input type="number" min="0" max="72" value={form.noShowGraceHours||0}
                      onChange={v=>sf('noShowGraceHours',v)} placeholder="0" />
                  </Field>
                  {(form.noShowPolicy === 'FIXED_AMOUNT') && (
                    <Field label={`Fixed Fee (${currency})`}>
                      <Input type="number" min="0" step="0.01" value={form.noShowFeeAmount||''}
                        onChange={v=>sf('noShowFeeAmount',v)} placeholder="e.g. 50" />
                    </Field>
                  )}
                  {(form.noShowPolicy === 'PERCENTAGE') && (
                    <Field label="Percentage (%)" hint="0–100">
                      <Input type="number" min="0" max="100" step="0.1" value={form.noShowFeePercentage||''}
                        onChange={v=>sf('noShowFeePercentage',v)} placeholder="e.g. 50" />
                    </Field>
                  )}
                </div>
                {form.noShowPolicy && form.noShowPolicy !== 'NONE' && (
                  <div style={{ marginTop:12, padding:'10px 14px', background:`${T.warning}12`,
                    borderRadius:9, border:`1px solid ${T.warning}30` }}>
                    <p style={{ fontSize:12, color:T.warning, fontWeight:600 }}>
                      {form.noShowPolicy === 'FIRST_NIGHT' && '⚡ The first night\'s room rate will be pre-filled as the suggested fee when marking a no-show.'}
                      {form.noShowPolicy === 'FULL_BOOKING' && '⚡ The full booking total will be pre-filled as the suggested fee when marking a no-show.'}
                      {form.noShowPolicy === 'PERCENTAGE' && `⚡ ${form.noShowFeePercentage||0}% of the total booking will be pre-filled as the suggested fee.`}
                      {form.noShowPolicy === 'FIXED_AMOUNT' && `⚡ ${currency} ${form.noShowFeeAmount||0} will be pre-filled as the suggested fee when marking a no-show.`}
                    </p>
                  </div>
                )}
              </div>

              {/* ── Desktop App (browser-only: hidden inside Electron) ──────── */}
              {/* {!window.pms && (
                <DesktopAppSection />
              )} */}

              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <Btn variant="primary" size="lg" disabled={saving} onClick={saveProperty}>
                  {saving ? '⏳ Saving…' : '💾 Save Changes'}
                </Btn>
              </div>
            </div>
          )}

          {/* ── INVOICING & TAX ─────────────────────────────────────────── */}
          {tab === 'invoicing' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

              {/* Logo */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <SectionTitle icon="🖼️" title="Property Logo" />
                <p style={{ fontSize:12, color:T.muted, marginBottom:14, lineHeight:1.5 }}>
                  Shown on both the folio receipt and the invoice header. PNG or JPG works best with a transparent or white background.
                </p>
                {!pid && (
                  <div style={{ background:T.warningPale, border:`1px solid ${T.warning}30`, borderRadius:10, padding:'10px 14px', marginBottom:14 }}>
                    <p style={{ fontSize:12, color:T.warning }}>⚠️ Save your property first before uploading a logo.</p>
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'center', gap:18 }}>
                  <div style={{
                    width:120, height:90, borderRadius:10, border:`1.5px dashed ${T.border}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    background:T.surface, overflow:'hidden', flexShrink:0,
                  }}>
                    {form.logoUrl
                      ? <img src={form.logoUrl} alt="Property logo" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
                      : <span style={{ fontSize:28, opacity:.35 }}>🏨</span>}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <input ref={logoFileRef} type="file" accept="image/*" hidden
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); }} />
                    <Btn variant="primary" disabled={!pid || logoUploading}
                      onClick={() => logoFileRef.current?.click()}>
                      {logoUploading ? '⏳ Uploading…' : form.logoUrl ? '🔄 Replace Logo' : '⬆️ Upload Logo'}
                    </Btn>
                    {form.logoUrl && (
                      <Btn variant="secondary" disabled={logoUploading} onClick={handleLogoRemove}>🗑️ Remove Logo</Btn>
                    )}
                  </div>
                </div>
              </div>

              {/* Tax identity */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <SectionTitle icon="🪪" title="Tax Identity" />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                  <Field label="TIN / TVA Number" hint="Printed on every invoice">
                    <Input value={form.tin||''} onChange={v=>sf('tin',v)} placeholder="e.g. 103420905" />
                  </Field>
                  <Field label="VAT Rate (%)" hint="0 disables VAT entirely">
                    <Input type="number" min="0" max="100" step="0.01" value={form.vatRate??18}
                      onChange={v=>sf('vatRate',v)} placeholder="18" />
                  </Field>
                  <Field label="Tourism Tax Rate (%)" hint="0 disables it — not every business charges this">
                    <Input type="number" min="0" max="100" step="0.01" value={form.tourismTaxRate??3}
                      onChange={v=>sf('tourismTaxRate',v)} placeholder="3" />
                  </Field>
                </div>
                <div style={{ marginTop:10, padding:'10px 14px', background:`${T.teal}10`, borderRadius:9, border:`1px solid ${T.teal}30` }}>
                  <p style={{ fontSize:11.5, color:T.navy, lineHeight:1.5 }}>
                    ℹ️ Accommodation (room charges) is taxed at the <strong>Tourism Tax Rate</strong>; every other service (restaurant, gym,
                    swimming, conference, laundry, etc.) is taxed at the <strong>VAT Rate</strong>. Each charge is taxed automatically based on
                    its category — the Invoice and folio show both as separate lines. The defaults (3% / 18%) match Rwanda's hospitality tax
                    rules — set either to 0 if it doesn't apply to your business.
                  </p>
                </div>
              </div>

              {/* Invoice footer */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <SectionTitle icon="🏦" title="Invoice Footer" />
                <Field label="Bank Account & Other Footer Details" hint="Printed at the bottom of every invoice — bank account number, bank name, SWIFT code, etc.">
                  <Input rows={5} value={form.invoiceFooterText||''} onChange={v=>sf('invoiceFooterText',v)}
                    placeholder={'Réf. Bancaire: BANK ACCOUNT NUMBER: 0000000000,\nBANK ACCOUNT NAME: YOUR COMPANY LTD, BANK NAME: EQUITY BANK,\nSWIFT CODE: EQBLRWRW, TIN&TVA No 000000000'} />
                </Field>
                {/* Phase 4 — currency mismatch warning */}
                {form.invoiceFooterText &&
                 (form.currency || 'RWF') !== 'USD' &&
                 /USD/i.test(form.invoiceFooterText) && (
                  <div style={{ background:'#FEF5E7', border:'1px solid #E07B0044', borderRadius:8, padding:'8px 12px', marginTop:8, fontSize:11, color:'#92400E', display:'flex', gap:8, alignItems:'flex-start' }}>
                    <span>⚠️</span>
                    <span>
                      Your footer text contains <strong>"USD"</strong> but this property's currency is <strong>{form.currency || 'RWF'}</strong>.
                      Please update your bank details to remove the incorrect currency label so invoices show the correct currency.
                    </span>
                  </div>
                )}
              </div>

              {/* Document templates — Proforma Terms & Payment Request wording */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <SectionTitle icon="📄" title="Document Templates" />
                <p style={{ fontSize:11, color:T.muted, marginBottom:14, lineHeight:1.5 }}>
                  The layout (logo, header, columns, tax breakdown) is shared across every Proforma Invoice and Payment Request — only
                  the wording below is editable. Both fields start pre-filled with sensible starter text — edit it to match your hotel's
                  actual policies, or clear a field to fall back to the built-in default. A specific contracted company can
                  override either of these in Corporate Accounts → Advanced.
                </p>
                <Field label="Proforma Invoice — Terms & Conditions" hint="One line per bullet point — deposit policy, cancellation policy, accepted payment methods, etc.">
                  <Input rows={5} value={form.proformaTermsText||''} onChange={v=>sf('proformaTermsText',v)} />
                </Field>
                <Field label="Payment Request — Body Paragraph" hint={
                  <>Merge fields: <code>{'{company}'}</code> <code>{'{property}'}</code> <code>{'{amountWords}'}</code> <code>{'{amountNumeric}'}</code> <code>{'{currency}'}</code> <code>{'{totalTax}'}</code> <code>{'{reason}'}</code> <code>{'{invoiceNumber}'}</code></>
                }>
                  <Input rows={3} value={form.paymentRequestBodyTemplate||''} onChange={v=>sf('paymentRequestBodyTemplate',v)} />
                </Field>
                <Field label="Payment Request — Signer Role" hint='Shown under the signature line, e.g. "Manager", "General Manager", "Accountant"'>
                  <Input value={form.paymentRequestSignerRole||''} onChange={v=>sf('paymentRequestSignerRole',v)} placeholder="Manager" />
                </Field>
              </div>

              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <Btn variant="primary" size="lg" disabled={saving} onClick={saveProperty}>
                  {saving ? '⏳ Saving…' : '💾 Save Changes'}
                </Btn>
              </div>
            </div>
          )}

          {/* ── ADVANCED ────────────────────────────────────────────────── */}
          {tab === 'advanced' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

              {/* Audit Logging */}
              {/* <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <SectionTitle icon="📋" title="Audit Logging" />
                <p style={{ fontSize:12, color:T.muted, marginBottom:16, lineHeight:1.5 }}>
                  Only critical changes are recorded — creating, updating, or deleting data
                  (reservations, guests, rooms, payments, etc.). Opening pages and viewing lists
                  is not logged, so the trail stays focused on actions that matter.
                </p>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'14px 18px', borderRadius:12,
                  background: form.auditLoggingEnabled ? `${T.success}10` : `${T.muted}10`,
                  border: `1px solid ${form.auditLoggingEnabled ? T.success : T.border}40` }}>
                  <div>
                    <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>
                      {form.auditLoggingEnabled ? '✅ Audit Logging Enabled' : '⛔ Audit Logging Disabled'}
                    </p>
                    <p style={{ fontSize:11, color:T.muted, marginTop:3 }}>
                      {form.auditLoggingEnabled
                        ? 'Create, update, and delete actions are recorded. View them in Audit Logs.'
                        : 'No staff activity is being recorded. Re-enable to resume tracking.'}
                    </p>
                  </div>
                  <button onClick={() => sf('auditLoggingEnabled', !form.auditLoggingEnabled)}
                    style={{ padding:'10px 22px', borderRadius:99, border:'none', cursor:'pointer',
                      fontFamily:FONTS.body, fontSize:12, fontWeight:700, transition:'all .2s',
                      background: form.auditLoggingEnabled ? T.danger : T.success,
                      color: '#fff' }}>
                    {form.auditLoggingEnabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
                {!form.auditLoggingEnabled && (
                  <div style={{ marginTop:10, padding:'10px 14px', background:'#FEF3C7',
                    borderRadius:9, border:'1px solid #FDE68A' }}>
                    <p style={{ fontSize:12, color:'#92400E', fontWeight:600 }}>
                      ⚠️ With audit logging disabled, there is no way to track what staff have done.
                      This is not recommended for production environments.
                    </p>
                  </div>
                )}
              </div> */}

              {/* Clear audit history */}
              <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, padding:'20px 22px', boxShadow:SHADOW.sm }}>
                <SectionTitle icon="🗑️" title="Clear Audit History" />
                <p style={{ fontSize:12, color:T.muted, marginBottom:16, lineHeight:1.5 }}>
                  Remove all existing audit log entries for this property. Use this once to clear
                  old page-view noise from before the logging rules were tightened. New entries
                  will only be created for create, update, and delete actions going forward.
                </p>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12,
                  padding:'14px 18px', borderRadius:12, background:`${T.danger}08`, border:`1px solid ${T.danger}25` }}>
                  <div>
                    <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>Truncate all logs</p>
                    <p style={{ fontSize:11, color:T.muted, marginTop:3, maxWidth:420, lineHeight:1.5 }}>
                      Permanently deletes every audit record for this property. This action cannot be undone.
                    </p>
                  </div>
                  <Btn variant="danger" disabled={!pid || truncating} onClick={handleTruncateAuditLogs}>
                    {truncating ? <><Spinner size={16} color="white" /> Clearing…</> : '🗑️ Clear All Logs'}
                  </Btn>
                </div>
              </div>

            </div>
          )}
          {/* ── END ADVANCED ────────────────────────────────────────────── */}

        </div>
        {/* ── END main content ────────────────────────────────────────── */}

      </div>
      {/* ── END body grid ───────────────────────────────────────────────── */}

      {/* ── Room Type Modal ───────────────────────────────────────────────── */}
      <Modal open={rtModal} onClose={() => setRtModal(false)}
        title={editRt ? `Edit — ${editRt.name}` : '+ New Room Type'} width={660}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:13 }}>
          <Field label="Name" required><Input value={rtForm.name||''} onChange={v=>srf('name',v)} placeholder="Deluxe King" /></Field>
          <Field label="Code" hint="Short code for boards"><Input value={rtForm.code||''} onChange={v=>srf('code',v.toUpperCase())} placeholder="DLX" /></Field>
          <Field label={`Base Rate (${currency}/night)`} required><Input type="number" min="0" value={rtForm.baseRate||''} onChange={v=>srf('baseRate',v)} placeholder="120" /></Field>
          <Field label={`Extra Person Rate (${currency}/night)`} hint="Separate fixed tariff used instead of the base rate once guest count exceeds Max Occupancy">
            <Input type="number" min="0" value={rtForm.extraPersonRate??''} onChange={v=>srf('extraPersonRate',v)} placeholder="e.g. 70000 (leave blank if not offered)" />
          </Field>
          <Field label="Bed Configuration"><Input value={rtForm.bedConfiguration||''} onChange={v=>srf('bedConfiguration',v)} placeholder="1 King Bed" /></Field>
          <Field label="Max Adults"><Input type="number" min="1" max="10" value={rtForm.maxAdults||2} onChange={v=>srf('maxAdults',v)} /></Field>
          <Field label="Max Children"><Input type="number" min="0" max="10" value={rtForm.maxChildren||1} onChange={v=>srf('maxChildren',v)} /></Field>
          <Field label="Max Occupancy"><Input type="number" min="1" value={rtForm.maxOccupancy||3} onChange={v=>srf('maxOccupancy',v)} /></Field>
          <Field label="Room Size (m²)"><Input type="number" min="0" value={rtForm.size||''} onChange={v=>srf('size',v)} placeholder="28" /></Field>
          <Field label="Sort Order" hint="Lower = shown first"><Input type="number" min="0" value={rtForm.sortOrder||0} onChange={v=>srf('sortOrder',v)} /></Field>
          <div style={{ gridColumn:'1/-1' }}>
            <Field label="Amenities (comma-separated)">
              <Input value={rtForm.amenities||''} onChange={v=>srf('amenities',v)} placeholder="AC, Minibar, Smart TV, Bathtub, City View, Safe..." />
            </Field>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <Field label="Description">
              <Input rows={3} value={rtForm.description||''} onChange={v=>srf('description',v)} placeholder="Spacious room with panoramic views..." />
            </Field>
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:16 }}>
          <Btn variant="secondary" onClick={() => setRtModal(false)} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} onClick={saveRt} style={{ flex:2 }}>
            {saving ? 'Saving…' : editRt ? '💾 Update' : '+ Create Room Type'}
          </Btn>
        </div>
      </Modal>

      {/* ── Room Modal ───────────────────────────────────────────────────── */}
      <Modal open={rModal} onClose={() => setRModal(false)}
        title={editRoom ? `Edit Room ${editRoom.roomNumber}` : '+ Add Room'} width={520}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:13 }}>
          <Field label="Room Number" required><Input value={rForm.roomNumber||''} onChange={v=>srrf('roomNumber',v)} placeholder="101" /></Field>
          <Field label="Room Type" required>
            <Select value={rForm.roomTypeId||''} onChange={v=>srrf('roomTypeId',v)}>
              <option value="">— Select —</option>
              {roomTypes.map(rt=><option key={rt.id} value={rt.id}>{rt.code?`${rt.code} — `:''}{rt.name}</option>)}
            </Select>
          </Field>
          <Field label="Floor"><Input value={rForm.floor||''} onChange={v=>srrf('floor',v)} placeholder="1" /></Field>
          <Field label="Wing / Block"><Input value={rForm.wing||''} onChange={v=>srrf('wing',v)} placeholder="Main, East…" /></Field>
          <Field label="Status">
            <Select value={rForm.status||'available'} onChange={v=>srrf('status',v)}>
              {ROOM_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Notes"><Input value={rForm.notes||''} onChange={v=>srrf('notes',v)} placeholder="Connecting room, Sea view…" /></Field>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:16 }}>
          <Btn variant="secondary" onClick={() => setRModal(false)} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} onClick={saveRoom} style={{ flex:2 }}>
            {saving ? 'Saving…' : editRoom ? '💾 Update Room' : '+ Add Room'}
          </Btn>
        </div>
      </Modal>

      <Toast message={toast} />
    </div>
  );
}